import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { useAdminDraft } from "@contexts/admin/AdminDraftsProvider";
import {
  phoneContentPadding,
  phoneContentPaddingBottom,
} from "@pages/AdminDashboard/AdminShell/layout";
import { CaseReference } from "@pages/AdminDashboard/CaseReference";
import { carrierTrackingUrl, orderInvoiceUrl } from "@pages/AdminDashboard/externalLinks";
import {
  formatAdminDate,
  formatAdminDateRange,
  formatAdminDateTime,
  formatMonthAndDay,
} from "@pages/AdminDashboard/formatAdminDate";
import { nameCase } from "@pages/AdminDashboard/ShipScreen/caseName";
import { describeShipAddress } from "@pages/AdminDashboard/ShipScreen/shipAddressFormat";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { phoneSearchFormLayout } from "@pages/AdminDashboard/theme/phoneSearchForm";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button, ButtonLook, ButtonSize } from "@pages/AdminDashboard/ui/Button";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { CheckboxCard } from "@pages/AdminDashboard/ui/CheckboxCard";
import { ConfirmDialog } from "@pages/AdminDashboard/ui/ConfirmDialog";
import { ContextStrip } from "@pages/AdminDashboard/ui/ContextStrip";
import { EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import {
  ExternalLink,
  ShortIdentifier,
  shortenIdentifier,
} from "@pages/AdminDashboard/ui/DataTable/cells";
import { FieldList } from "@pages/AdminDashboard/ui/FieldList";
import { Note, NoteTone } from "@pages/AdminDashboard/ui/Note";
import { PageHeader } from "@pages/AdminDashboard/ui/PageHeader";
import { MedicationList } from "@pages/AdminDashboard/ui/MedicationList";
import { StatusBadge, Tag } from "@pages/AdminDashboard/ui/StatusBadge";
import { TextField } from "@pages/AdminDashboard/ui/TextField";
import { PatientIdentity } from "@pages/AdminDashboard/UserEmailLink";
import {
  cancelRedoOrder,
  findRedoOrder,
  loadOrderMedications,
  loadRedoInvoice,
  loadRedoPrescriptions,
  replaceRedoOrder,
  resendRedoOrder,
} from "@store/admin/adminAction";
import { redoCancelRejected, redoOrderSelected } from "@store/admin/adminSlice";
import {
  OrderMedicationsState,
  RedoCancelState,
  RedoInvoiceState,
  RedoPrescription,
  RedoReplaceState,
  RedoScreenState,
  ShipCase,
} from "@types-runway/admin";
import { Order } from "@types-runway/case";
import { RootState } from "@types-runway/state";

import { findConflictingOrders } from "./conflictingOrders";

const { bannerTone, color, radius, typeScale } = adminTokens;
const [, spaceSm, spaceMd, spaceLg, spaceXl] = adminTokens.spacing;
const welcomeTitleSize = "16px";
const orderIdFieldWidth = 240;
const barPaddingStart = 16;
const barPaddingEnd = 4;
const dotSize = 12;
const dotBorder = 2;
const lineWidth = 1.5;
const notShippedLabel = "Not shipped yet";
const noValue = "—";
const notYetSentLabel = "Not yet sent";
const noLinkLabel = "No link to another Order";
const countWords = ["two", "three", "four", "five", "six", "seven", "eight", "nine"];
const reshipLabel = "Replace Order (reship)";
const resendLabel = "Re-send to pharmacy";
const shippedStatuses = ["out_for_delivery", "completed"];
const pharmacyStatuses = ["new", "in_progress"];

const isShipped = (order: Order) => shippedStatuses.includes(order.status);

const Page = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spaceXl}px;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const FindBar = styled.form<{ $isPhone: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spaceSm}px;
  padding: ${barPaddingEnd}px ${barPaddingEnd}px ${barPaddingEnd}px ${barPaddingStart}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.pill}px;
  ${({ $isPhone }) => $isPhone && phoneSearchFormLayout}
`;

const OrderIdSlot = styled.div`
  width: ${orderIdFieldWidth}px;
`;

const Welcome = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${barPaddingEnd}px;
  padding: ${spaceXl}px 0;
  text-align: center;
  color: ${color.inkSoft};
`;

const WelcomeTitle = styled.span`
  color: ${color.navy};
  font-size: ${welcomeTitleSize};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: ${spaceXl}px;
  align-items: start;
`;

const Timeline = styled.ol`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TimelineItem = styled.li`
  display: flex;
  gap: ${spaceMd}px;
`;

const Track = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  width: ${dotSize + 2}px;
`;

const Dot = styled.span<{ $isCancelled: boolean }>`
  width: ${dotSize}px;
  height: ${dotSize}px;
  margin-top: ${spaceLg + 2}px;
  border-radius: 50%;
  box-sizing: border-box;
  background-color: ${({ $isCancelled }) => ($isCancelled ? color.surface : color.tealDark)};
  border: ${dotBorder}px solid
    ${({ $isCancelled }) => ($isCancelled ? color.inkSoft : color.tealDark)};
`;

const Line = styled.span`
  flex: 1;
  width: ${lineWidth}px;
  background-color: ${color.border};
`;

const OrderRow = styled.button<{ $isChosen: boolean; $isConflicting: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  margin-bottom: ${spaceSm + 2}px;
  padding: ${spaceMd}px ${spaceLg - 2}px;
  text-align: left;
  color: ${color.navy};
  font-family: inherit;
  cursor: pointer;
  background-color: ${({ $isChosen }) => ($isChosen ? color.canvas : color.surface)};
  border: 1px solid
    ${({ $isChosen, $isConflicting }) => {
      if ($isChosen) return color.periwinkle;

      return $isConflicting ? bannerTone.error.border : color.border;
    }};
  border-radius: ${radius.row}px;

  &:focus-visible {
    ${focusOutline}
  }
`;

const RowHead = styled.span`
  display: flex;
  align-items: center;
  gap: ${spaceSm}px;
  flex-wrap: wrap;
`;

const OrderLabel = styled.span`
  font-family: ${adminFontFamily.medium};
  font-size: 15px;
`;

const Soft = styled.span`
  color: ${color.inkSoft};
`;

const Facts = styled.span`
  color: ${color.inkSoft};
  font-size: ${typeScale.meta.fontSize};
`;

const History = styled.span<{ $isConflicting: boolean }>`
  color: ${({ $isConflicting }) => ($isConflicting ? bannerTone.error.text : color.inkSoft)};
  font-family: ${adminFontFamily.medium};
  font-size: ${typeScale.meta.fontSize};
`;

const Spacer = styled.span`
  flex: 1;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SelectedOrderId = styled.span`
  font-family: ${adminFontFamily.light};
  font-size: 24px;
`;

const PickerGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceSm}px;
`;

const PickerTitle = styled.span`
  font-family: ${adminFontFamily.medium};
  font-size: ${typeScale.meta.fontSize};
`;

const SectionLabel = styled.span`
  color: ${color.inkSoft};
  font-size: 12px;
`;

const PrescriptionStatus = styled.span`
  color: ${color.inkSoft};
`;

// On a phone each Order is a card of the design's phone record card: 16px corners, padded 14px
// above and below and 16px at each side (REDOUI-R85, PHONE-R17).
const phoneCardRadius = 16;
const phoneCardPaddingY = 14;
const phoneCardGap = 10;
const phoneHitTarget = 44;

const PhoneListHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spaceSm}px;
`;

const PhoneListTitle = styled.h2`
  margin: 0;
  font-family: ${adminFontFamily.medium};
  font-size: 16px;
  font-weight: normal;
`;

const PhoneOrderList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spaceMd}px;
  margin: ${spaceMd}px 0 0;
  padding: 0;
  list-style: none;
`;

const PhoneOrderCard = styled.li<{ $isChosen: boolean; $isConflicting: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${phoneCardGap}px;
  min-width: 0;
  padding: ${phoneCardPaddingY}px ${spaceLg}px;
  background-color: ${color.surface};
  border: 1px solid
    ${({ $isChosen, $isConflicting }) => {
      if ($isChosen) return color.periwinkle;

      return $isConflicting ? bannerTone.error.border : color.border;
    }};
  border-radius: ${phoneCardRadius}px;
`;

const PhoneOrderChoice = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${barPaddingEnd}px;
  width: 100%;
  min-height: ${phoneHitTarget}px;
  padding: 0;
  text-align: left;
  color: inherit;
  font: inherit;
  cursor: pointer;
  background: none;
  border: 0;

  &:focus-visible {
    ${focusOutline}
  }
`;

const PhoneOrderDetails = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${phoneCardGap}px;
`;

const SelectedMark = styled.span`
  align-self: flex-start;
`;

// On a phone the chosen Order's Buttons sit in a bar that stays at the bottom of the window while
// the page scrolls; it ends the page, so the last card always ends above it. Its margins cancel
// the padding of the shell's content area, its direct container, so it reaches the window's edges
// (REDOUI-R90…R93).
const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${spaceMd}px;
  margin: 0 -${phoneContentPadding}px -${phoneContentPaddingBottom}px;
  padding: ${spaceLg}px ${phoneContentPadding}px calc(${spaceLg}px + env(safe-area-inset-bottom));
  background-color: ${color.surface};
  border-top: 1px solid ${color.border};

  & > button {
    width: 100%;
  }
`;

const describeTrip = (trip?: ShipCase["trip"] & { destination?: string }) => {
  if (!trip?.from) return "No trip date";

  const dates = formatAdminDateRange(trip.from, trip.to);

  return trip.destination ? `${trip.destination} · ${dates}` : dates;
};

const invoicePaymentWords = {
  paid: "paid",
  unpaid: "unpaid",
  failed: "payment couldn't be confirmed",
} as const;

// The Order's invoice and the payment state of that invoice, the invoice ID alone while it
// loads; an Order with no invoice, such as one whose charge was declined, reads None, which
// the field's Invoice label explains (REDOUI-R17, R78, R79, R81, XCUT-R34).
const describeOrderInvoice = (order: Order, invoice: RedoInvoiceState) => {
  if (!order.invoiceId) return "None";
  if (invoice.status === "idle" || invoice.status === "pending") return order.invoiceId;

  return `${order.invoiceId} · ${invoicePaymentWords[invoice.status]}`;
};

const describeTracking = (order: Order) => {
  if (order.tracking_number) {
    return order.carrier
      ? `${order.carrier.toUpperCase()} · ${order.tracking_number}`
      : order.tracking_number;
  }

  return order.status === "cancelled" ? noValue : notShippedLabel;
};

// The Selected Order's tracking opens the shipment at its carrier; the timeline keeps it as text,
// since a timeline row is chosen as a whole (XCUT-R24, R25).
const OrderTracking = ({ order }: { order: Order }) => {
  const trackingUrl = order.tracking_number
    ? carrierTrackingUrl(order.carrier, order.tracking_number)
    : null;
  if (!trackingUrl) return <>{describeTracking(order)}</>;

  return <ExternalLink href={trackingUrl}>{describeTracking(order)}</ExternalLink>;
};

// The Order's invoice opens at the payment vendor that holds it once its invoice has answered
// with that vendor (XCUT-R26…R28).
const OrderInvoice = ({ order, invoice }: { order: Order; invoice: RedoInvoiceState }) => {
  const provider = "provider" in invoice ? invoice.provider : null;
  const invoiceUrl = order.invoiceId ? orderInvoiceUrl(order.invoiceId, provider) : null;
  if (!invoiceUrl || invoice.status === "idle" || invoice.status === "pending") {
    return <>{describeOrderInvoice(order, invoice)}</>;
  }

  return (
    <>
      <ExternalLink href={invoiceUrl}>{order.invoiceId}</ExternalLink>
      {` · ${invoicePaymentWords[invoice.status]}`}
    </>
  );
};

const describeFacts = (order: Order) =>
  [
    order.shipping_method ?? noValue,
    describeTracking(order),
    formatAdminDate(order.createdAt),
  ].join(" · ");

const findPredecessor = (order: Order, orders: Order[]) =>
  orders.find((other) => other.supersededByOrderId === order.id);

// A corrective Order follows an Order that shipped (REDOUI-R52).
const isCorrectiveOrder = (order: Order, orders: Order[]) => {
  const predecessor = findPredecessor(order, orders);

  return Boolean(predecessor && isShipped(predecessor));
};

const describeCorrectiveHistory = (order: Order, predecessor: Order) => {
  const sameInvoice = order.invoiceId && order.invoiceId === predecessor.invoiceId;

  return `Corrective Order for OID ${shortenIdentifier(predecessor.order_id)}${sameInvoice ? " · same invoice" : ""}`;
};

// "Replaced by OID b72e0c4a · Sep 24", "Replacement for OID a41c9e07", or nothing (REDOUI-R15). A
// corrective Order always reads "Corrective Order for", even when it was replaced in turn
// (REDOUI-R52); an Order that shipped reads "Reshipped by" (REDOUI-R51).
const describeHistory = (order: Order, orders: Order[]) => {
  const predecessor = findPredecessor(order, orders);
  if (predecessor && isShipped(predecessor)) return describeCorrectiveHistory(order, predecessor);

  const successor = orders.find((other) => other.id === order.supersededByOrderId);
  if (successor) {
    const verb = isShipped(order) ? "Reshipped by" : "Replaced by";

    return `${verb} OID ${shortenIdentifier(successor.order_id)} · ${formatMonthAndDay(successor.createdAt)}`;
  }

  return predecessor ? `Replacement for OID ${shortenIdentifier(predecessor.order_id)}` : "";
};

interface ConflictAlertProps {
  shipCase: ShipCase;
  conflictingOrders: Order[];
}

// The alert for a Case with a second active Order that no reship explains, worded for two Orders
// (REDOUI-R65) or for three or more (REDOUI-R84).
const ConflictAlert = ({ shipCase, conflictingOrders }: ConflictAlertProps) => {
  const orderLabels = conflictingOrders.map((order) => `OID ${shortOrderId(order.order_id)}`);
  const countWord = countWords[conflictingOrders.length - 2] ?? String(conflictingOrders.length);
  const isPair = conflictingOrders.length === 2;
  const named = `${orderLabels.slice(0, -1).join(", ")} and ${orderLabels[orderLabels.length - 1]}`;

  return (
    <Banner
      tone="error"
      title={`${nameCase(shipCase)} has ${countWord} active Orders.`}
      body={`${named} ${isPair ? "are both active and neither is" : "are all active and none is"} a reship of a shipped Order. The User could receive two shipments. Investigate before cancelling or replacing.`}
    />
  );
};

interface OrderTimelineProps {
  orders: Order[];
  conflictingOrders: Order[];
  chosenOrderId: number;
  onChoose: (orderId: number) => void;
}

const OrderTimeline = ({
  orders,
  conflictingOrders,
  chosenOrderId,
  onChoose,
}: OrderTimelineProps) => (
  <section aria-label="Orders on this Case">
    <Card title="Orders on this Case" aside={<Soft>All Orders for the Case, newest first</Soft>}>
      <Timeline>
        {orders.map((order, position) => {
          const isCancelled = order.status === "cancelled";
          const isConflicting = conflictingOrders.some(
            (conflicting) => conflicting.id === order.id
          );
          const history = describeHistory(order, orders) || (isConflicting ? noLinkLabel : "");

          return (
            <TimelineItem key={order.id}>
              <Track>
                <Dot data-testid="redo-dot" aria-hidden="true" $isCancelled={isCancelled} />
                {position < orders.length - 1 && <Line aria-hidden="true" />}
              </Track>
              <OrderRow
                type="button"
                $isChosen={order.id === chosenOrderId}
                $isConflicting={isConflicting}
                aria-current={order.id === chosenOrderId ? "true" : undefined}
                onClick={() => onChoose(order.id)}
              >
                <RowHead>
                  <OrderLabel>
                    OID <ShortIdentifier value={order.order_id} />
                  </OrderLabel>
                  <Soft>#{order.id}</Soft>
                  <Spacer />
                  {isCorrectiveOrder(order, orders) && <Tag>Reship</Tag>}
                  <StatusBadge status={order.status ?? notYetSentLabel} />
                </RowHead>
                <Facts>{describeFacts(order)}</Facts>
                {history && <History $isConflicting={isConflicting}>{history}</History>}
              </OrderRow>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Card>
  </section>
);

interface PhoneOrderCardsProps extends OrderTimelineProps {
  chosenDetails: ReactNode;
}

// On a phone the Orders stand as cards, newest first; the chosen one is marked "Selected" and
// holds what the Selected Order card shows on a wider screen (REDOUI-R85…R89).
const PhoneOrderCards = ({
  orders,
  conflictingOrders,
  chosenOrderId,
  chosenDetails,
  onChoose,
}: PhoneOrderCardsProps) => (
  <section aria-label="Orders on this Case">
    <PhoneListHead>
      <PhoneListTitle>Orders on this Case</PhoneListTitle>
      <Soft>All Orders for the Case, newest first</Soft>
    </PhoneListHead>
    <PhoneOrderList role="list">
      {orders.map((order) => {
        const isChosen = order.id === chosenOrderId;
        const isConflicting = conflictingOrders.some((conflicting) => conflicting.id === order.id);
        const history = describeHistory(order, orders) || (isConflicting ? noLinkLabel : "");

        return (
          <PhoneOrderCard
            key={order.id}
            data-testid="redo-order-card"
            $isChosen={isChosen}
            $isConflicting={isConflicting}
          >
            <PhoneOrderChoice
              type="button"
              aria-current={isChosen ? "true" : undefined}
              onClick={() => onChoose(order.id)}
            >
              <RowHead>
                <OrderLabel>
                  OID <ShortIdentifier value={order.order_id} />
                </OrderLabel>
                <Spacer />
                {isCorrectiveOrder(order, orders) && <Tag>Reship</Tag>}
                <StatusBadge status={order.status ?? notYetSentLabel} />
              </RowHead>
              <Facts>
                #{order.id} · created {formatAdminDate(order.createdAt)}
              </Facts>
            </PhoneOrderChoice>
            {isChosen && (
              <PhoneOrderDetails aria-label="Selected Order">
                <SelectedMark>
                  <Tag>Selected</Tag>
                </SelectedMark>
                {chosenDetails}
              </PhoneOrderDetails>
            )}
            {history && <History $isConflicting={isConflicting}>{history}</History>}
          </PhoneOrderCard>
        );
      })}
    </PhoneOrderList>
  </section>
);

const describePrescription = (prescription: RedoPrescription) =>
  prescription.title ?? prescription.name ?? prescription.case_offering_id;

// What the replacement's picker reads while the prescriptions load, when they fail, or when there are
// none to offer (REDOUI-R31).
const PrescriptionsStatus = ({
  prescriptions,
}: {
  prescriptions: RedoScreenState["prescriptions"];
}) => {
  if (prescriptions.status === "pending" || prescriptions.status === "idle") {
    return <PrescriptionStatus>Loading prescriptions…</PrescriptionStatus>;
  }
  if (prescriptions.status === "failed") {
    return <PrescriptionStatus>The prescriptions couldn&apos;t be loaded.</PrescriptionStatus>;
  }

  return <PrescriptionStatus>No prescriptions on this Order.</PrescriptionStatus>;
};

const CardActions = styled.div`
  display: flex;
  gap: ${spaceMd}px;
  padding-top: ${spaceLg}px;
  border-top: 1px solid ${color.border};
`;

// The steps the confirmation of a cancellation numbers: the pharmacy is asked only for an Order
// that is new or in progress (REDOUI-R22).
const describeCancelSteps = (order: Order) => {
  const markedCancelled = "It is marked cancelled here and stays on the Case as history.";
  if (!pharmacyStatuses.includes(order.status)) return [markedCancelled];

  return [
    `It is ${order.status.replace(/_/g, " ")} at the pharmacy, so we ask the pharmacy to stop it.`,
    markedCancelled,
  ];
};

const shortOrderId = (orderId: string) => shortenIdentifier(orderId);

interface CancelBannerProps {
  cancel: RedoCancelState;
  onTryAgain: () => void;
  onReship?: () => void;
}

// What the last cancellation did (REDOUI-R25…R27).
const CancelBanner = ({ cancel, onTryAgain, onReship }: CancelBannerProps) => {
  if (cancel.status === "cancelled") {
    return (
      <Banner
        tone="success"
        title={`OID ${shortOrderId(cancel.orderId)} is cancelled.`}
        body={`${
          cancel.wasAtPharmacy
            ? "The pharmacy confirmed the cancellation."
            : "The Order is marked cancelled here; the pharmacy was not asked."
        } If the User still needs their medication, use Replace Order.`}
      />
    );
  }
  if (cancel.status === "rejected") {
    return (
      <Banner
        tone="error"
        title={`Can't cancel: OID ${shortOrderId(cancel.orderId)} has already shipped.`}
        body="A shipped Order is never recalled. To send the right medication or ship to a corrected address, replace it: the shipped Order stays as is and a corrective Order ships under the same payment."
        action={onReship && { label: reshipLabel, onAction: onReship }}
      />
    );
  }
  if (cancel.status === "failed") {
    return (
      <Banner
        tone="error"
        title="Cancel didn't go through. Nothing changed."
        body={`Reason from the server: “${cancel.reason}”. OID ${shortOrderId(cancel.orderId)} is still ${cancel.orderStatus}.`}
        action={{ label: "Try again", onAction: onTryAgain }}
      />
    );
  }

  return null;
};

// The steps the confirmation of a replacement numbers (REDOUI-R32).
const describeReplaceSteps = (order: Order) => {
  const shortId = shortOrderId(order.order_id);
  const firstStep = (() => {
    if (pharmacyStatuses.includes(order.status)) {
      return `OID ${shortId} is cancelled at the pharmacy and kept on the Case as history.`;
    }
    if (order.status === "cancelled") {
      return `OID ${shortId} is already cancelled and stays on the Case as history.`;
    }

    return `OID ${shortId} is marked cancelled here and kept on the Case as history.`;
  })();

  return [
    firstStep,
    `A new Order is created and paid by the original invoice ${order.invoiceId}. The User is not charged again.`,
    "The new Order is sent to the pharmacy with the prescriptions above.",
  ];
};

interface ReplaceDialogText {
  eyebrow: string;
  title: string;
  steps: string[];
  note: string;
  dismissLabel: string;
  confirmLabel: string;
  pendingLabel: string;
}

// The steps of the confirmation of a reship (REDOUI-R43).
const describeReshipSteps = (order: Order, shipCase: ShipCase) => {
  const address = shipCase.shippingAddress
    ? `: ${describeShipAddress(shipCase.shippingAddress)}.`
    : ".";

  return [
    `OID ${shortOrderId(order.order_id)} has shipped, so it is left exactly as it is.`,
    `A corrective Order is created under the same invoice ${order.invoiceId}: two Orders, one charge, absorbed by Runway.`,
    `The corrective Order ships to the Case's current address${address}`,
  ];
};

// What the confirmation of a replacement says: a reship for an Order that has shipped
// (REDOUI-R42), a replacement for another (REDOUI-R31).
const describeReplaceDialog = (order: Order, shipCase: ShipCase): ReplaceDialogText => {
  const shortId = shortOrderId(order.order_id);
  if (isShipped(order)) {
    return {
      eyebrow: "Replace a shipped Order",
      title: `Reship OID ${shortId}?`,
      steps: describeReshipSteps(order, shipCase),
      note: "Wrong address? Fix it in Ship first so the corrective Order picks it up.",
      dismissLabel: "Keep as is",
      confirmLabel: "Reship",
      pendingLabel: "Reshipping…",
    };
  }

  return {
    eyebrow: "Replace Order",
    title: `Replace OID ${shortId}?`,
    steps: describeReplaceSteps(order),
    note: `The User keeps ${nameCase(shipCase)}. They get a new tracking number and no duplicate order confirmation.`,
    dismissLabel: "Keep current Order",
    confirmLabel: "Replace Order",
    pendingLabel: "Replacing…",
  };
};

// The action that sends an Order to the pharmacy again, "Re-sending…" while it is pending (REDOUI-R39, R61).
const describeResendAction = (isPending: boolean, onAction: () => void) =>
  isPending
    ? { label: resendLabel, onAction, isPending: true as const, pendingLabel: "Re-sending…" }
    : { label: resendLabel, onAction };

interface ReplaceBannerProps {
  replace: RedoReplaceState;
  onTryAgain: () => void;
  onResend: () => void;
}

// What the last replacement did (REDOUI-R36…R39).
const ReplaceBanner = ({ replace, onTryAgain, onResend }: ReplaceBannerProps) => {
  if (replace.status === "replaced") {
    return (
      <Banner
        tone="success"
        title={`Replaced. OID ${shortOrderId(replace.successorOrderId)} was sent to the pharmacy.`}
        body={`Paid by the original invoice ${replace.invoiceId}; the User was not charged. They keep Case ${shortenIdentifier(replace.caseId)} and will get the new tracking number when it ships. No duplicate order confirmation is sent.`}
      />
    );
  }
  if (replace.status === "alreadyReplaced") {
    return (
      <Banner
        tone="info"
        title="Already replaced. No new Order was created."
        body={`OID ${shortOrderId(replace.orderId)} was already superseded by OID ${shortOrderId(replace.existingOrderId)} (${formatAdminDateTime(replace.createdAt)}). That existing Order is selected below; check its status before doing anything else.`}
        action={replace.resend && describeResendAction(replace.resend.isPending, onResend)}
      />
    );
  }
  if (replace.status === "failed") {
    return (
      <Banner
        tone="error"
        title="Replace didn't go through. Nothing changed."
        body={`Reason from the server: “${replace.reason}”. OID ${shortOrderId(replace.orderId)} is still ${replace.orderStatus}.`}
        action={{ label: "Try again", onAction: onTryAgain }}
      />
    );
  }
  if (replace.status === "notSent" || replace.status === "resending") {
    return (
      <Banner
        tone="warning"
        title={`OID ${shortOrderId(replace.successorOrderId)} was created but not sent to the pharmacy.`}
        body={`It is paid by the original invoice ${replace.invoiceId}. Re-send it from here; replacing again is not needed. Pharmacy response: “${replace.reason}”.`}
        action={describeResendAction(replace.status === "resending", onResend)}
      />
    );
  }

  return null;
};

interface ReplacePickerProps {
  prescriptions: RedoScreenState["prescriptions"];
  uncheckedIds: string[];
  onToggle: (prescriptionId: string) => void;
}

// The prescriptions to send with the new Order, each checked until the admin clears it (REDOUI-R31).
const ReplacePicker = ({ prescriptions, uncheckedIds, onToggle }: ReplacePickerProps) => (
  <PickerGroup role="group" aria-label="Prescriptions to send">
    <PickerTitle>Prescriptions to send</PickerTitle>
    {prescriptions.status === "loaded" && prescriptions.items.length > 0 ? (
      prescriptions.items.map((prescription) => (
        <CheckboxCard
          key={prescription.case_offering_id}
          title={describePrescription(prescription)}
          detail={prescription.directions}
          checked={!uncheckedIds.includes(prescription.case_offering_id)}
          onChange={() => onToggle(prescription.case_offering_id)}
        />
      ))
    ) : (
      <PrescriptionsStatus prescriptions={prescriptions} />
    )}
  </PickerGroup>
);

interface OrderNote {
  tone: NoteTone;
  text: string;
}

// What the card says about an Order the admin may not replace, or must replace (REDOUI-R71…R73,
// R75): a missing invoice comes first, because the note of an error sends the admin to a Button
// the card then does not have.
const describeOrderNote = (order: Order): OrderNote | null => {
  if (!order.invoiceId) {
    return {
      tone: "info",
      text: "Replace Order isn't available: this Order has no invoice to fund a replacement.",
    };
  }
  if (order.status === "error") {
    return {
      tone: "error",
      text: "The pharmacy rejected this Order. An error is final at the pharmacy, so it can't be re-submitted. Replace it to send a new Order under the same payment.",
    };
  }

  return null;
};

interface SelectedOrderDetailsProps {
  order: Order;
  shipCase: ShipCase;
  medications?: OrderMedicationsState;
  invoice: RedoInvoiceState;
}

// What the admin reads about the chosen Order before acting on it, at every width
// (REDOUI-R17, R18, R71…R75, R89).
const SelectedOrderDetails = ({
  order,
  shipCase,
  medications,
  invoice,
}: SelectedOrderDetailsProps) => {
  const note = describeOrderNote(order);

  return (
    <>
      <FieldList
        fields={[
          { label: "Shipping method", value: order.shipping_method ?? noValue },
          { label: "Tracking", value: <OrderTracking order={order} /> },
          { label: "Invoice", value: <OrderInvoice order={order} invoice={invoice} /> },
          {
            label: "Ships to",
            value: shipCase.shippingAddress
              ? describeShipAddress(shipCase.shippingAddress)
              : noValue,
          },
        ]}
      />
      <SectionLabel>Medications</SectionLabel>
      <MedicationList medications={medications} />
      {note && <Note tone={note.tone}>{note.text}</Note>}
    </>
  );
};

const hasOrderActions = (order: Order, canReplace: boolean) =>
  canReplace || order.status !== "cancelled";

interface OrderActionsProps {
  order: Order;
  size?: ButtonSize;
  cancelLook: ButtonLook;
  isBusy: boolean;
  canReplace: boolean;
  onReplace: () => void;
  onCancel: () => void;
}

// The replacement and the cancellation of the chosen Order (REDOUI-R19, R29, R30, R40, R95).
const OrderActions = ({
  order,
  size,
  cancelLook,
  isBusy,
  canReplace,
  onReplace,
  onCancel,
}: OrderActionsProps) => (
  <>
    {canReplace && (
      <Button
        look={order.status === "error" ? "commit" : "primary"}
        size={size}
        disabled={isBusy}
        onClick={onReplace}
      >
        {isShipped(order) ? reshipLabel : "Replace Order"}
      </Button>
    )}
    {order.status !== "cancelled" && (
      <Button look={cancelLook} size={size} disabled={isBusy} onClick={onCancel}>
        Cancel Order
      </Button>
    )}
  </>
);

interface SelectedOrderCardProps {
  order: Order;
  details: ReactNode;
  actions: ReactNode;
}

const SelectedOrderCard = ({ order, details, actions }: SelectedOrderCardProps) => (
  <section aria-label="Selected Order">
    <Card eyebrow="Selected Order" aside={<StatusBadge status={order.status ?? notYetSentLabel} />}>
      <Heading>
        <SelectedOrderId>
          OID <ShortIdentifier value={order.order_id} />
        </SelectedOrderId>
        <Soft>
          #{order.id} · created {formatAdminDate(order.createdAt)}
        </Soft>
      </Heading>
      {details}
      {actions && <CardActions>{actions}</CardActions>}
    </Card>
  </section>
);

// The Redo screen: finds an Order by its Order ID or its Case ID and shows the Orders of its
// Case and the one chosen (REDOUI-R1…R18).
const RedoScreen = () => {
  const isPhone = useIsPhoneLayout();
  const dispatch = useDispatch();
  const location = useLocation();
  const { lookup, shipCase, orders, selectedOrderId, prescriptions } = useSelector<
    RootState,
    RedoScreenState
  >((state) => state.admin.redoScreen);
  const invoice = useSelector<RootState, RedoInvoiceState>((state) => state.admin.redoInvoice);
  const cancel = useSelector<RootState, RedoCancelState>((state) => state.admin.redoCancel);
  const replace = useSelector<RootState, RedoReplaceState>((state) => state.admin.redoReplace);
  const orderMedications = useSelector<RootState, Record<number, OrderMedicationsState>>(
    (state) => state.admin.orderMedications
  );
  const entry = new URLSearchParams(location.search).get("case") ?? "";

  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isConfirmingReplace, setIsConfirmingReplace] = useState(false);
  const [uncheckedIds, setUncheckedIds] = useState<string[]>([]);
  // What the admin entered and searched stays while other screens of the admin are open (REDOUI-R76).
  const [orderIdField, setOrderIdField] = useAdminDraft("redo.orderIdField", entry);
  const [searchedText, setSearchedText] = useAdminDraft("redo.searchedText", entry);

  const findOrder = (query: string) => {
    setSearchedText(query);
    dispatch(findRedoOrder({ query }));
  };

  // A Case ID in the page address (a link from the Ship screen) is searched at once; a new link
  // to the same screen carries a new location key, so it searches again (REDOUI-R5).
  useEffect(() => {
    if (!entry) return;

    setOrderIdField(entry);
    findOrder(entry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, location.key]);

  const newestFirst = [...orders].sort((first, second) => second.id - first.id);
  const conflictingOrders = findConflictingOrders(orders);
  const chosenOrder = orders.find((order) => order.id === selectedOrderId) ?? newestFirst[0];
  const chosenOrderId = chosenOrder?.id;

  useEffect(() => {
    if (lookup !== "found" || chosenOrderId === undefined) return;
    if (prescriptions.status !== "idle") return;

    dispatch(loadRedoPrescriptions({ orderId: chosenOrderId }));
  }, [dispatch, lookup, chosenOrderId, prescriptions.status]);

  // What the chosen Order sent to the pharmacy, asked for once per Order (MEDS-R10, REDOUI-R18).
  const chosenMedications =
    chosenOrderId === undefined ? undefined : orderMedications[chosenOrderId];
  useEffect(() => {
    if (lookup !== "found" || chosenOrderId === undefined || chosenMedications) return;

    dispatch(loadOrderMedications({ orderId: chosenOrderId }));
  }, [dispatch, lookup, chosenOrderId, chosenMedications]);

  // The payment state of the chosen Order's invoice; an Order with no invoice has none (REDOUI-R77).
  const chosenInvoiceId = chosenOrder?.invoiceId;
  useEffect(() => {
    if (lookup !== "found" || chosenOrderId === undefined || !chosenInvoiceId) return;
    if (invoice.status !== "idle") return;

    dispatch(loadRedoInvoice({ orderId: chosenOrderId }));
  }, [dispatch, lookup, chosenOrderId, chosenInvoiceId, invoice.status]);

  // The confirmation closes when the cancellation it started ends, whatever the outcome (REDOUI-R25, R27).
  const wasCancelPending = useRef(false);
  const isCancelPending = cancel.status === "pending";
  useEffect(() => {
    if (wasCancelPending.current && !isCancelPending) setIsConfirmingCancel(false);
    wasCancelPending.current = isCancelPending;
  }, [isCancelPending]);

  // The confirmation of a replacement closes when the replacement it started ends (REDOUI-R36…R38).
  const wasReplacePending = useRef(false);
  const isReplacePending = replace.status === "pending";
  useEffect(() => {
    if (wasReplacePending.current && !isReplacePending) setIsConfirmingReplace(false);
    wasReplacePending.current = isReplacePending;
  }, [isReplacePending]);

  const isResendPending =
    replace.status === "resending" ||
    (replace.status === "alreadyReplaced" && Boolean(replace.resend?.isPending));
  const isBusy = isCancelPending || isReplacePending || isResendPending;
  const canReplace = Boolean(chosenOrder?.invoiceId && shipCase?.caseId);
  const replaceDialog =
    chosenOrder && shipCase ? describeReplaceDialog(chosenOrder, shipCase) : null;
  const checkedPrescriptions =
    prescriptions.status === "loaded"
      ? prescriptions.items.filter(
          (prescription) => !uncheckedIds.includes(prescription.case_offering_id)
        )
      : [];

  const requestReplace = () => {
    setUncheckedIds([]);
    setIsConfirmingReplace(true);
  };

  const toggleReplacePrescription = (prescriptionId: string) =>
    setUncheckedIds((previous) =>
      previous.includes(prescriptionId)
        ? previous.filter((checkedId) => checkedId !== prescriptionId)
        : [...previous, prescriptionId]
    );

  const confirmReplace = () =>
    chosenOrder &&
    shipCase?.caseId &&
    chosenOrder.invoiceId &&
    dispatch(
      replaceRedoOrder({
        id: chosenOrder.id,
        orderId: chosenOrder.order_id,
        status: chosenOrder.status,
        invoiceId: chosenOrder.invoiceId,
        caseId: shipCase.caseId,
        prescriptions: checkedPrescriptions,
      })
    );

  const resendReplacement = () => {
    if (replace.status === "notSent" || replace.status === "resending") {
      dispatch(
        resendRedoOrder({
          orderId: replace.successorOrderId,
          prescriptions: replace.prescriptions,
          invoiceId: replace.invoiceId,
          caseId: replace.caseId,
        })
      );
    }
    if (replace.status === "alreadyReplaced" && replace.resend) {
      const { prescriptions, invoiceId, caseId } = replace.resend;
      dispatch(
        resendRedoOrder({ orderId: replace.existingOrderId, prescriptions, invoiceId, caseId })
      );
    }
  };

  // A shipped Order is never cancelled: nothing is asked and the admin is told why (REDOUI-R26).
  const requestCancel = () => {
    if (!chosenOrder) return;
    if (isShipped(chosenOrder)) {
      dispatch(redoCancelRejected({ orderId: chosenOrder.order_id }));
      return;
    }

    setIsConfirmingCancel(true);
  };

  const confirmCancel = () =>
    chosenOrder &&
    dispatch(
      cancelRedoOrder({
        id: chosenOrder.id,
        orderId: chosenOrder.order_id,
        status: chosenOrder.status,
      })
    );

  const chooseOrder = (orderId: number) => dispatch(redoOrderSelected(orderId));
  const hasActions = Boolean(chosenOrder && hasOrderActions(chosenOrder, canReplace));
  const chosenDetails = chosenOrder && shipCase && (
    <SelectedOrderDetails
      order={chosenOrder}
      shipCase={shipCase}
      medications={chosenMedications}
      invoice={invoice}
    />
  );
  const renderOrderActions = (cancelLook: ButtonLook, size?: ButtonSize) =>
    chosenOrder && (
      <OrderActions
        order={chosenOrder}
        size={size}
        cancelLook={cancelLook}
        isBusy={isBusy}
        canReplace={canReplace}
        onReplace={requestReplace}
        onCancel={requestCancel}
      />
    );

  const submitFind = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = orderIdField.trim();
    if (!query || lookup === "pending") return;

    findOrder(query);
  };

  return (
    <Page>
      <PageHeader
        title="Redo"
        subtitle="Cancel or replace an Order. The User is never charged again."
        aside={
          <FindBar
            $isPhone={isPhone}
            role="search"
            aria-label="Find an Order"
            onSubmit={submitFind}
          >
            <OrderIdSlot>
              <TextField
                label="Order ID or Case ID"
                isLabelHidden
                value={orderIdField}
                onChange={(event) => setOrderIdField(event.target.value)}
              />
            </OrderIdSlot>
            <Button
              look="primary"
              type="submit"
              disabled={lookup === "pending" || !orderIdField.trim()}
              isPending={lookup === "pending"}
              pendingLabel="Find Order"
            >
              Find Order
            </Button>
          </FindBar>
        }
      />

      {lookup === "idle" && (
        <Welcome>
          <WelcomeTitle>Find an Order by its Order ID or Case ID</WelcomeTitle>
          <span>The Orders on its Case open here.</span>
        </Welcome>
      )}

      {lookup === "not_found" && (
        <div data-testid="redo-order-not-found" role="alert" aria-live="assertive">
          <EmptyStateCard
            title="Order not found"
            message={`No Order or Case has the ID ${searchedText}. Check the spelling.`}
          />
        </div>
      )}

      {lookup === "error" && (
        <Banner
          tone="error"
          title="The Order lookup failed."
          body="Nothing changed. Try again."
          action={{
            label: "Try again",
            onAction: () => findOrder(orderIdField.trim() || searchedText),
          }}
        />
      )}

      {lookup === "found" && shipCase && chosenOrder && (
        <>
          <CancelBanner
            cancel={cancel}
            onTryAgain={() => setIsConfirmingCancel(true)}
            onReship={canReplace ? requestReplace : undefined}
          />
          <ReplaceBanner
            replace={replace}
            onTryAgain={requestReplace}
            onResend={resendReplacement}
          />
          {conflictingOrders.length > 0 && (
            <ConflictAlert shipCase={shipCase} conflictingOrders={conflictingOrders} />
          )}
          <ContextStrip
            label="Case context"
            identifiers={[
              <CaseReference key="case" caseNumber={shipCase.id} caseId={shipCase.caseId} />,
              ...(shipCase.User ? [<PatientIdentity key="patient" patient={shipCase.User} />] : []),
              describeTrip(shipCase.trip),
            ]}
          />
          {isPhone ? (
            <>
              <PhoneOrderCards
                orders={newestFirst}
                conflictingOrders={conflictingOrders}
                chosenOrderId={chosenOrder.id}
                chosenDetails={chosenDetails}
                onChoose={chooseOrder}
              />
              {hasActions && (
                <ActionBar role="group" aria-label="Actions on the chosen Order">
                  {renderOrderActions("destructiveOutline", "dialog")}
                </ActionBar>
              )}
            </>
          ) : (
            <Columns>
              <OrderTimeline
                orders={newestFirst}
                conflictingOrders={conflictingOrders}
                chosenOrderId={chosenOrder.id}
                onChoose={chooseOrder}
              />
              <SelectedOrderCard
                order={chosenOrder}
                details={chosenDetails}
                actions={hasActions && renderOrderActions("destructive")}
              />
            </Columns>
          )}
          {replaceDialog && (
            <ConfirmDialog
              isOpen={isConfirmingReplace}
              eyebrow={replaceDialog.eyebrow}
              title={replaceDialog.title}
              consequences={replaceDialog.steps}
              note={replaceDialog.note}
              dismissLabel={replaceDialog.dismissLabel}
              confirmLabel={replaceDialog.confirmLabel}
              pendingLabel={replaceDialog.pendingLabel}
              isPending={isReplacePending}
              isConfirmDisabled={checkedPrescriptions.length === 0}
              onDismiss={() => setIsConfirmingReplace(false)}
              onConfirm={confirmReplace}
            >
              <ReplacePicker
                prescriptions={prescriptions}
                uncheckedIds={uncheckedIds}
                onToggle={toggleReplacePrescription}
              />
            </ConfirmDialog>
          )}
          <ConfirmDialog
            isOpen={isConfirmingCancel}
            eyebrow="Cancel Order"
            title={`Cancel OID ${shortOrderId(chosenOrder.order_id)}?`}
            consequences={describeCancelSteps(chosenOrder)}
            note="This can't be undone. If the User still needs their medication, use Replace Order instead."
            dismissLabel="Keep Order"
            confirmLabel="Cancel Order"
            pendingLabel="Cancelling…"
            isDestructive
            isPending={isCancelPending}
            onDismiss={() => setIsConfirmingCancel(false)}
            onConfirm={confirmCancel}
          />
        </>
      )}
    </Page>
  );
};

export default RedoScreen;
